import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { convertToDeal } from "../lib/convertToDeal";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DocumentsTab from "../components/deal/DocumentsTab";
import StabilisationHealth from "../components/StabilisationHealth";
import { ArrowLeft, Loader2, ShieldAlert, Stethoscope, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Same tier-badge presentation as DistressWatchlist.jsx's diagnosis panel —
// kept as a small local copy rather than a shared import, matching this
// codebase's existing per-page constant-map convention (see
// DistressWatchlist.jsx's own docstring-style comment on TIER_STYLES).
const TIER_STYLES = {
    healthy: "bg-green-100 text-green-800 border-green-300",
    low_leverage: "bg-green-100 text-green-800 border-green-300",
    adequate: "bg-green-100 text-green-800 border-green-300",
    tight: "bg-amber-100 text-amber-800 border-amber-300",
    moderate_leverage: "bg-amber-100 text-amber-800 border-amber-300",
    narrative_flagged: "bg-amber-100 text-amber-800 border-amber-300",
    strained: "bg-red-100 text-red-800 border-red-300",
    negative_net_worth: "bg-red-100 text-red-800 border-red-300",
    high_leverage: "bg-red-100 text-red-800 border-red-300",
    insufficient_data: "bg-slate-100 text-slate-600 border-slate-300",
};
const TIER_LABELS = {
    negative_net_worth: "negative net worth",
    high_leverage: "high leverage",
    moderate_leverage: "moderate leverage",
    low_leverage: "low leverage",
    narrative_flagged: "narrative flagged",
    insufficient_data: "insufficient data",
};

function TierBadge({ tier }) {
    const cls = TIER_STYLES[tier] || TIER_STYLES.insufficient_data;
    const label = TIER_LABELS[tier] || tier;
    return <Badge variant="outline" className={cls}>{label}</Badge>;
}

const STAGE_ORDER = ["initial_review", "diagnosis_complete", "engaged", "structuring", "completed", "lost", "withdrawn"];
const STAGE_LABELS = {
    initial_review: "Initial Review", diagnosis_complete: "Diagnosis Complete",
    engaged: "Engaged", structuring: "Structuring",
    completed: "Completed", lost: "Lost", withdrawn: "Withdrawn",
};

const CDD_STATUS_STYLES = {
    not_started: "bg-slate-100 text-slate-600 border-slate-300",
    pending: "bg-amber-100 text-amber-800 border-amber-300",
    complete: "bg-green-100 text-green-800 border-green-300",
    expired: "bg-red-100 text-red-700 border-red-300",
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB");
    } catch {
        return iso;
    }
}

export default function DistressedMandateDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { hasSurface } = useAuth();
    const [mandate, setMandate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingStage, setSavingStage] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/distressed/mandates/${id}`);
            setMandate(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const updateStage = async (stage) => {
        setSavingStage(true);
        try {
            const { data } = await api.patch(`/distressed/mandates/${id}`, { stage });
            setMandate((m) => ({ ...m, ...data }));
            toast.success(`Stage updated to ${STAGE_LABELS[stage] || stage}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSavingStage(false);
        }
    };

    if (loading || !mandate) {
        return (
            <div className="flex items-center justify-center py-24 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading mandate…
            </div>
        );
    }

    const diagnosis = mandate.diagnosis;

    return (
        <div data-testid="distressed-mandate-detail-page">
            <PageHeader
                title={mandate.company_name}
                subtitle={mandate.company_number}
            >
                <Link
                    to="/distressed/mandates"
                    className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1"
                    data-testid="back-to-mandates-link"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Mandates
                </Link>
                <Link to={`/distressed/cashflow?mandate=${mandate.id}`} data-testid="mandate-cashflow-link">
                    <Button variant="outline" size="sm">Cash flow forecasts</Button>
                </Link>
                <Link to={`/distressed/structuring?mandate=${mandate.id}`} data-testid="mandate-structuring-link">
                    <Button variant="outline" size="sm">Route structuring</Button>
                </Link>
                {hasSurface("deal_management") && (
                    <Button size="sm" onClick={() => convertToDeal(`/distressed/mandates/${mandate.id}/convert-to-deal`, nav)} data-testid="mandate-convert-to-deal">
                        {mandate.deal_id ? "Open deal" : "Convert to deal"}
                    </Button>
                )}
                <Select value={mandate.stage} onValueChange={updateStage} disabled={savingStage}>
                    <SelectTrigger className="w-44" data-testid="mandate-stage-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STAGE_ORDER.map((s) => (
                            <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </PageHeader>

            <div className="p-6 lg:p-8 space-y-6 max-w-5xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* CDD status */}
                    <Card data-testid="mandate-cdd-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <ShieldCheck className="h-4 w-4" /> CDD status
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-2">
                                <Badge
                                    variant="outline"
                                    className={CDD_STATUS_STYLES[mandate.cdd?.cdd_status] || CDD_STATUS_STYLES.not_started}
                                    data-testid="mandate-cdd-status"
                                >
                                    {(mandate.cdd?.cdd_status || "not_started").replace("_", " ")}
                                </Badge>
                                {mandate.cdd?.risk_rating && (
                                    <Badge variant="outline">{mandate.cdd.risk_rating}</Badge>
                                )}
                            </div>
                            {!mandate.cdd?.contact_id && (
                                <p className="text-xs text-muted-foreground mt-2">
                                    No CRM contact linked to this mandate yet — CDD cannot be tracked
                                    until one is. Compliance owns the CDD workflow itself.
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Diagnosis snapshot */}
                    <Card data-testid="mandate-diagnosis-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <Stethoscope className="h-4 w-4" /> Diagnosis snapshot
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {!diagnosis ? (
                                <p className="text-xs text-muted-foreground">
                                    {mandate.watchlist_entry_id
                                        ? "No diagnosis has been run yet on the source watchlist entry."
                                        : "This mandate wasn't promoted from a watchlist entry, so no diagnosis is available. Run one from the Watchlist page."}
                                </p>
                            ) : !diagnosis.accounts_available ? (
                                <p className="text-xs text-amber-700 flex items-start gap-1">
                                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                    {diagnosis.error || "No diagnosis data available."}
                                </p>
                            ) : (
                                <div className="flex flex-wrap items-center gap-2">
                                    <TierBadge tier={diagnosis.liquidity?.tier} />
                                    <TierBadge tier={diagnosis.structural?.tier} />
                                    <TierBadge tier={diagnosis.profitability?.tier} />
                                    <span className="text-xs text-muted-foreground ml-1">
                                        {diagnosis.red_flags?.length || 0} red flag{diagnosis.red_flags?.length === 1 ? "" : "s"}
                                    </span>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* TUPE calculations */}
                <Card data-testid="mandate-tupe-card">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <ShieldAlert className="h-4 w-4" /> TUPE calculations
                        </CardTitle>
                        <Button asChild variant="outline" size="sm">
                            <Link to="/distressed/tupe-calculator">Open TUPE calculator</Link>
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {(!mandate.tupe_calculations || mandate.tupe_calculations.length === 0) ? (
                            <p className="text-xs text-muted-foreground">
                                No TUPE calculations saved against this mandate yet.
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {mandate.tupe_calculations.map((calc) => (
                                    <div
                                        key={calc.id}
                                        className="flex items-center justify-between text-sm border rounded-md px-3 py-2"
                                        data-testid={`mandate-tupe-calc-${calc.id}`}
                                    >
                                        <span>{calc.label || calc.transaction_route}</span>
                                        <span className="text-xs text-muted-foreground">{fmtDate(calc.created_at)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {["engaged", "structuring", "completed", "lost", "withdrawn"].includes(mandate.stage) && <StabilisationHealth mandateId={mandate.id} />}

                {/* Notes */}
                {mandate.notes && (
                    <Card data-testid="mandate-notes-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Notes</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-muted-foreground">{mandate.notes}</p>
                        </CardContent>
                    </Card>
                )}

                {/* Data room */}
                <DocumentsTab parentType="distressed_mandate" parentId={mandate.id} />
            </div>
        </div>
    );
}
